import { useState, useEffect, useRef, useCallback } from 'react';
import { speakBusinessWarning, speakLawWarning, isVoiceMuted, toggleVoiceMute, initVoices } from '../../../utils/voiceAlert';
import { toast } from '../../ui/simple-toaster';

const VOICE_ALERT_ADVANCE_SECONDS = 300;

/**
 * Hook for voice alert logic — fires audio warnings before compliance thresholds.
 */
export function useVoiceAlerts(driverState, currentSegment, isBusinessRegime, elapsedTime) {
  const [voiceMuted, setVoiceMuted] = useState(() => isVoiceMuted());
  const businessAlertFiredRef = useRef(false);
  const bizLayerAlertFiredRef = useRef(false);
  const lawAlertFiredRef = useRef(false);

  useEffect(() => {
    initVoices();
  }, []);

  useEffect(() => {
    if (!driverState || !currentSegment || currentSegment.segment_type !== 'WORK') {
      businessAlertFiredRef.current = false;
      bizLayerAlertFiredRef.current = false;
      lawAlertFiredRef.current = false;
      return;
    }

    const realContinuousWork = (driverState.continuous_work_seconds || 0) + elapsedTime;

    if (isBusinessRegime) {
      const warningBefore = driverState.warning_before_seconds || 600;
      const workLimit = driverState.work_limit_seconds || 10800;
      const screenWarningAt = workLimit - warningBefore;
      const voiceAlertAt = screenWarningAt - VOICE_ALERT_ADVANCE_SECONDS;

      if (realContinuousWork >= voiceAlertAt && !businessAlertFiredRef.current) {
        businessAlertFiredRef.current = true;
        speakBusinessWarning();
      }
    } else {
      // FRHV: Check optional business layer first (fires earliest)
      const bizLayer = driverState.business_layer;
      if (bizLayer && bizLayer.enabled) {
        const bizWarningAt = (bizLayer.work_limit_seconds - bizLayer.warning_before_seconds) - VOICE_ALERT_ADVANCE_SECONDS;
        if (realContinuousWork >= bizWarningAt && !bizLayerAlertFiredRef.current) {
          bizLayerAlertFiredRef.current = true;
          speakBusinessWarning();
        }
      }

      // FRHV: Shadow threshold
      const shadowThreshold = driverState.time_until_shadow_sec !== undefined
        ? realContinuousWork + driverState.time_until_shadow_sec
        : 17700;
      const lawThreshold = driverState.time_until_rest_required_sec !== undefined
        ? realContinuousWork + driverState.time_until_rest_required_sec
        : 18900;

      const shadowVoiceAt = shadowThreshold - VOICE_ALERT_ADVANCE_SECONDS;
      if (realContinuousWork >= shadowVoiceAt && !businessAlertFiredRef.current) {
        businessAlertFiredRef.current = true;
        speakBusinessWarning();
      }

      const lawVoiceAt = lawThreshold - VOICE_ALERT_ADVANCE_SECONDS;
      if (realContinuousWork >= lawVoiceAt && !lawAlertFiredRef.current) {
        lawAlertFiredRef.current = true;
        speakLawWarning();
      }
    }
  }, [driverState, currentSegment, isBusinessRegime]);

  const handleVoiceMuteToggle = useCallback(() => {
    const newMuted = toggleVoiceMute();
    setVoiceMuted(newMuted);
    toast(newMuted ? 'Voice alerts muted' : 'Voice alerts enabled', { duration: 2000 });
  }, []);

  return { voiceMuted, handleVoiceMuteToggle };
}
